import { ColumnDef } from '@tanstack/react-table'
import { ClientDataTable } from 'components/ClientDataTable/ClientDataTable'
import { FbModal } from 'components/UI/Modal'
import { useMemo } from 'react'
import { CSVRow, InternalColumns } from '../importer-types'

export function ImportWizardDialogConfirmDeselectMatched({
  show,
  headers,
  data,
  onCancel,
  onAccept,
}: {
  show: boolean
  headers: Record<string, string>
  data: CSVRow[]
  onCancel: () => void
  onAccept: () => void
}) {
  const columnDefs: ColumnDef<CSVRow>[] = useMemo(() => {
    return [
      {
        id: 'input_data',
        header: 'Your Data',
        cell: ({ row }) => {
          const assembledAddress = [
            row.original[headers.street],
            row.original[headers.city],
            row.original[headers.state],
            row.original[headers.zip],
          ]
            .filter(Boolean)
            .join(', ')

          const name = row.original[headers.name] || '-'
          const domain = row.original[headers.domain] || '-'
          const address =
            row.original[headers.full_address] || assembledAddress || '-'

          return (
            <div className="grid grid-cols-[4rem_1fr] gap-x-2 gap-y-0.5 text-sm">
              <span className="text-xxs text-fg-muted font-medium uppercase">
                Name
              </span>
              <span className="text-fg truncate">{name}</span>
              <span className="text-xxs text-fg-muted font-medium uppercase">
                Domain
              </span>
              <span className="text-fg truncate">{domain}</span>
              <span className="text-xxs text-fg-muted font-medium uppercase">
                Address
              </span>
              <span className="text-fg truncate">{address}</span>
            </div>
          )
        },
      },
      {
        id: 'matched_chain',
        header: 'Matched Chain',
        cell: ({ row }) => {
          const chain = row.original[InternalColumns.MATCHED_CHAIN]
          if (!chain) return <span className="text-sm text-fg-muted">-</span>

          return (
            <div className="grid grid-cols-[4rem_1fr] gap-x-2 gap-y-0.5 text-sm">
              <span className="text-xxs text-fg-muted font-medium uppercase">
                Name
              </span>
              <span className="text-fg truncate">{chain.name}</span>
              <span className="text-xxs text-fg-muted font-medium uppercase">
                Domain
              </span>
              <span className="text-fg truncate">{chain.domain || '-'}</span>
              <span className="text-xxs text-fg-muted font-medium uppercase">
                Cities
              </span>
              <span
                className="text-fg truncate"
                title={chain.city_summary || '-'}
              >
                {chain.city_summary || '-'}
              </span>
              <span className="text-xxs text-fg-muted font-medium uppercase">
                States
              </span>
              <span
                className="text-fg truncate"
                title={chain.state_summary || '-'}
              >
                {chain.state_summary || '-'}
              </span>
            </div>
          )
        },
      },
      {
        id: 'matched_door',
        header: 'Matched Door',
        cell: ({ row }) => {
          const door = row.original[InternalColumns.MATCHED_DOOR]
          if (!door) return <span className="text-sm text-fg-muted">-</span>

          return (
            <div className="grid grid-cols-[4rem_1fr] gap-x-2 gap-y-0.5 text-sm">
              <span className="text-xxs text-fg-muted font-medium uppercase">
                Name
              </span>
              <span className="text-fg truncate">{door.name}</span>
              <span className="text-xxs text-fg-muted font-medium uppercase">
                Address
              </span>
              <span className="text-fg truncate">{door.address || '-'}</span>
              <span className="text-xxs text-fg-muted font-medium uppercase">
                City
              </span>
              <span className="text-fg truncate">{door.city || '-'}</span>
              <span className="text-xxs text-fg-muted font-medium uppercase">
                State
              </span>
              <span className="text-fg truncate">{door.state || '-'}</span>
              <span className="text-xxs text-fg-muted font-medium uppercase">
                Country
              </span>
              <span className="text-fg truncate">{door.country || '-'}</span>
            </div>
          )
        },
      },
    ]
  }, [headers])

  if (!show) {
    return null
  }

  return (
    <FbModal
      size="xl"
      title={'Review Deselected Matches'}
      description={
        'These matches will be removed. Review them below before confirming.'
      }
      disableCloseIcon
      open={true}
      cancelButtonText="Cancel"
      acceptButtonText="Confirm"
      onCancel={onCancel}
      onAccept={onAccept}
    >
      <ClientDataTable data={data} columns={columnDefs} enableSorting />
    </FbModal>
  )
}
